
import React from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Cloud, HardDrive, Bell, Filter, Users, CloudOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Settings = () => {
  // Default values for demonstration purposes
  const [autoDownload, setAutoDownload] = React.useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(true);
  const [defaultSaveLocation, setDefaultSaveLocation] = React.useState('downloads');
  const [attachmentSizeLimit, setAttachmentSizeLimit] = React.useState('10');
  const [filterDuplicates, setFilterDuplicates] = React.useState(true);
  const [offlineAccess, setOfflineAccess] = React.useState(false);
  const [trustedSenders, setTrustedSenders] = React.useState('');

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-medium">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Customize your attachment management preferences
        </p>
      </div>

      <Tabs defaultValue="general" className="w-full max-w-3xl">
        <TabsList className="mb-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="downloads">Downloads</TabsTrigger>
          <TabsTrigger value="filters">Filters</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>
                Configure how you want to be notified about new attachments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label className="text-base">Enable Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications when new attachments are available
                  </p>
                </div>
                <Switch 
                  checked={notificationsEnabled}
                  onCheckedChange={setNotificationsEnabled}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label className="text-base">Offline Access</Label>
                  <p className="text-sm text-muted-foreground">
                    Cache recent attachments for offline viewing
                  </p>
                </div>
                <Switch 
                  checked={offlineAccess}
                  onCheckedChange={setOfflineAccess}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="downloads" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Download Preferences</CardTitle>
              <CardDescription>
                Customize how attachments are downloaded
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label className="text-base">Auto-Download</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically download new attachments when they arrive
                  </p>
                </div>
                <Switch 
                  checked={autoDownload}
                  onCheckedChange={setAutoDownload}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="text-base">Default Save Location</Label>
                <Select 
                  value={defaultSaveLocation} 
                  onValueChange={setDefaultSaveLocation}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="downloads">Downloads Folder</SelectItem>
                    <SelectItem value="documents">Documents Folder</SelectItem>
                    <SelectItem value="desktop">Desktop</SelectItem>
                    <SelectItem value="custom">Custom Location</SelectItem>
                  </SelectContent>
                </Select>
                {defaultSaveLocation === 'custom' && (
                  <div className="flex items-center space-x-2 mt-2">
                    <Input placeholder="/home/user/attachments" />
                    <Button variant="outline" size="sm">Browse</Button>
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  Choose where downloaded attachments will be saved
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="filters" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filter Settings</CardTitle>
              <CardDescription>
                Manage how attachments are filtered and displayed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base">Attachment Size Limit (MB)</Label>
                <div className="flex items-center space-x-2">
                  <Input 
                    type="number" 
                    value={attachmentSizeLimit} 
                    onChange={(e) => setAttachmentSizeLimit(e.target.value)}
                    className="w-32" 
                  />
                  <span className="text-sm text-muted-foreground">MB</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Only show attachments smaller than this size
                </p>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label className="text-base">Filter Duplicates</Label>
                  <p className="text-sm text-muted-foreground">
                    Hide duplicate attachments with the same filename and size
                  </p>
                </div>
                <Switch 
                  checked={filterDuplicates}
                  onCheckedChange={setFilterDuplicates}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="text-base">Trusted Senders</Label>
                <Input 
                  placeholder="Enter email addresses (comma separated)"
                  value={trustedSenders}
                  onChange={(e) => setTrustedSenders(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Prioritize attachments from these email addresses
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="account" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>
                Manage your account details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label>Email</Label>
                  <div className="rounded-md border p-3 text-sm">
                    demo@example.com
                  </div>
                </div>
                
                <div className="pt-2">
                  <Button variant="destructive" size="sm">
                    <CloudOff className="mr-2 h-4 w-4" />
                    Revoke Gmail Access
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
};

export default Settings;
